import { useCallback, useEffect, useState } from "react";
import { Building2, Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useOperator } from "@/context/OperatorContext";
import { opApi, apiError } from "@/lib/api";

const REASONS = { business_suspended: "Paused", entitlement_expired: "No access" };

// One Morr account, several businesses in this app. Morr Access checks every switch.
export function BusinessSwitcher() {
  const op = useOperator()?.operator;
  const [busy, setBusy] = useState(false);
  const list = op?.businesses || [];
  const label = op?.app === "morrconnect" ? "Switch business" : "Switch shop";

  const go = useCallback(async (orgId) => {
    setBusy(true);
    try {
      await opApi.post("/operator/switch-business", { org_id: orgId });
      const u = new URL(window.location.href);
      u.searchParams.delete("org");
      window.location.replace(u.toString());
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Couldn't open that business.");
      setBusy(false);
    }
  }, []);

  // Bookmarks carry ?org= so they reopen the right business.
  useEffect(() => {
    const want = new URLSearchParams(window.location.search).get("org");
    if (want && op?.org_id && want !== op.org_id && list.some((b) => b.org_id === want && b.access !== false)) go(want);
  }, [op?.org_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!op || op.impersonating || op.role === "superadmin" || list.length < 2) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-none gap-2" data-testid="business-switcher-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
          <span className="hidden sm:inline">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none min-w-[260px]" data-testid="business-switcher-menu">
        <DropdownMenuLabel className="text-xs text-muted-foreground">{label}</DropdownMenuLabel>
        {list.map((b) => {
          const off = b.access === false;
          return (
            <DropdownMenuItem key={b.org_id} disabled={busy || off} onClick={() => b.org_id !== op.org_id && go(b.org_id)}
              data-testid={`business-switcher-item-${b.org_id}`}>
              <span className="w-4 mr-2">{b.org_id === op.org_id && <Check className="h-4 w-4" />}</span>
              <span className="flex-1 min-w-0">
                <span className="block truncate">{b.name}</span>
                <span className="block text-[11px] text-muted-foreground">{off ? (REASONS[b.reason] || "No access") : b.plan}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
        {op.morr_links?.apps && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild data-testid="business-switcher-add">
              <a href={op.morr_links.apps}><Plus className="h-4 w-4 mr-2" /> Add another business</a>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
